import type { GlobalEvents, ServerEvent, ServerEventResourceKind } from "@storytell/types";

const globalEventTarget = new EventTarget();

/**
 * Determines if an error is a network-related error based on the error message.
 * Used to detect connectivity issues for showing offline banners.
 */
export const isNetworkError = (error: unknown): boolean => {
  // Intentional abort (e.g., component unmount, navigation) is not a network error
  if (error instanceof Error && error.name === "AbortError") {
    return false;
  }

  // Timeout from AbortSignal.timeout IS a network-related issue
  if (error instanceof Error && error.name === "TimeoutError") {
    return true;
  }

  const message = String(error).toLowerCase();

  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("err_network") ||
    message.includes("err_internet") ||
    message.includes("err_connection") ||
    message.includes("fetch failed") ||
    message.includes("net::err_") ||
    message.includes("network request failed") ||
    message.includes("load failed") ||
    // HTTP/2 and protocol errors
    message.includes("err_http2") ||
    message.includes("protocol error") ||
    message.includes("connection reset") ||
    message.includes("connection closed") ||
    message.includes("stream terminated")
  );
};

// Helper function to dispatch custom events with data
export function dispatchGlobalEvent<T extends keyof GlobalEvents>(eventName: T, data: GlobalEvents[T]["data"]) {
  const customEvent = new CustomEvent(eventName, {
    detail: data,
  });
  globalEventTarget.dispatchEvent(customEvent);
}

const isGlobalEvent = <T extends keyof GlobalEvents>(
  event: Event,
  eventName: T
): event is CustomEvent<GlobalEvents[T]["data"]> => event instanceof CustomEvent && event.type === eventName;

// Helper function to listen for custom events
export function subscribeToGlobalEvent<T extends keyof GlobalEvents>(
  eventName: T,
  callback: (event: CustomEvent<GlobalEvents[T]["data"]>) => void
) {
  const handler = (event: Event) => {
    if (isGlobalEvent(event, eventName)) {
      callback(event);
    }
  };

  globalEventTarget.addEventListener(eventName, handler);

  // Return cleanup function
  return () => {
    globalEventTarget.removeEventListener(eventName, handler);
  };
}

// Subscribes to server-event-received and filters by resource kind, so each
// store doesn't repeat the cast and kind guard.
export function subscribeToServerEvent(kind: ServerEventResourceKind, handler: (event: ServerEvent) => void) {
  return subscribeToGlobalEvent("server-event-received", (event) => {
    const serverEvent = event.detail;
    if (serverEvent.resource.kind !== kind) return;
    handler(serverEvent);
  });
}
